'use client';
import React, { useState, useEffect } from 'react';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/app/components/PublicFooter';
import AppImage from '@/components/ui/AppImage';
import { Play, ExternalLink, Headphones, Music2, Filter, Loader2 } from 'lucide-react';

type ReleaseType = 'All' | 'Singles' | 'Albums' | 'EPs' | 'Collaborations';

interface Release {
  id: string;
  title: string;
  artist: string;
  featuredArtists: string | null;
  type: string;
  genre: string | null;
  year: string;
  isFeatured: boolean;
  streams: string;
  cover: string;
  coverAlt: string;
  links: {
    apple: string;
    spotify: string;
    youtube: string;
    soundcloud: string;
    audiomack: string;
  };
}

const platforms = [
  { name: 'Apple Music', href: 'https://music.apple.com/us/artist/hadatha/1611554470', color: '#FC3C44' },
  { name: 'SoundCloud', href: 'https://soundcloud.com/johnbruddatz', color: '#FF6600' },
  { name: 'Spotify', href: '#', color: '#1DB954' },
  { name: 'YouTube Music', href: '#', color: '#FF0000' },
  { name: 'Audiomack', href: '#', color: '#FF6600' },
  { name: 'Deezer', href: '#', color: '#A238FF' },
  { name: 'Tidal', href: '#', color: '#00FFFF' },
  { name: 'Boomplay', href: '#', color: '#FF4500' },
  { name: 'Amazon Music', href: '#', color: '#00A8E0' },
];

const filters: ReleaseType[] = ['All', 'Singles', 'Albums', 'EPs', 'Collaborations'];

export default function MusicPage() {
  const [activeFilter, setActiveFilter] = useState<ReleaseType>('All');
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/music')
      .then((r) => r.json())
      .then((data) => setReleases(data.releases ?? []))
      .catch(() => setReleases([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = releases.filter((r) => {
    if (activeFilter === 'All') return true;
    if (activeFilter === 'Singles') return r.type === 'Single';
    if (activeFilter === 'Collaborations') return r.artist.includes('feat.') || Boolean(r.featuredArtists);
    return r.type === activeFilter.slice(0, -1);
  });

  const featuredRelease = releases.find((r) => r.isFeatured) ?? releases[0];

  return (
    <div className="min-h-screen bg-background">
      <PublicTopbar />

      {/* Page header */}
      <section className="pt-32 pb-16 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Discography</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <h1 className="text-5xl font-800 text-foreground">Music</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={14} className="text-muted-foreground" />
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-3 py-1.5 text-xs font-600 rounded-lg transition-all ${
                    activeFilter === f
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted/70'
                  }`}>
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loading state */}
      {loading && (
        <section className="pb-24 bg-background">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex justify-center py-24">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        </section>
      )}

      {!loading && (
        <>
          {/* Featured release */}
          {featuredRelease && (
            <section className="pb-8 bg-background">
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
                <div className="relative rounded-2xl overflow-hidden bg-card border border-border card-glow">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="relative aspect-square lg:aspect-auto lg:min-h-[400px]">
                      <AppImage
                        src={featuredRelease.cover}
                        alt={featuredRelease.coverAlt}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/80 hidden lg:block" />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <span className="inline-block px-3 py-1 text-[10px] font-700 tracking-widest uppercase rounded-full bg-primary/20 text-primary border border-primary/30 mb-4 w-fit">
                        Latest Release · {featuredRelease.year}
                      </span>
                      <h2 className="text-4xl font-800 text-foreground mb-2">{featuredRelease.title}</h2>
                      <p className="text-muted-foreground mb-1">{featuredRelease.artist}</p>
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-xs text-muted-foreground">{featuredRelease.genre}</span>
                        <span className="text-border">·</span>
                        <Headphones size={12} className="text-muted-foreground" />
                        <span className="text-xs font-tabular text-muted-foreground">{featuredRelease.streams} streams</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <a
                          href={featuredRelease.links.apple}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 active:scale-95 transition-all justify-center">
                          <Play size={16} fill="currentColor" />
                          Apple Music
                        </a>
                        <a
                          href={featuredRelease.links.soundcloud}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-3 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 text-sm font-600 transition-all justify-center">
                          <ExternalLink size={14} />
                          SoundCloud
                        </a>
                        <a
                          href={featuredRelease.links.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-3 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 text-sm font-600 transition-all justify-center">
                          <Play size={14} />
                          YouTube
                        </a>
                        <a
                          href={featuredRelease.links.spotify}
                          className="flex items-center gap-2 px-4 py-3 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 text-sm font-600 transition-all justify-center">
                          <Music2 size={14} />
                          Spotify
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* All releases grid */}
          <section className="py-12 bg-background">
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
              {filtered.length === 0 ? (
                <div className="text-center py-24">
                  <Music2 size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No releases found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                  {filtered.map((release) => (
                    <div
                      key={release.id}
                      className="group cursor-pointer"
                      onClick={() => setSelectedRelease(selectedRelease?.id === release.id ? null : release)}>
                      <div className="relative aspect-square rounded-xl overflow-hidden bg-card border border-border card-glow-hover transition-all duration-200">
                        <AppImage
                          src={release.cover}
                          alt={release.coverAlt}
                          fill
                          className="object-cover group-hover:scale-110 transition-transform duration-500"
                          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 16vw"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center">
                            <Play size={20} className="text-primary-foreground translate-x-0.5" fill="currentColor" />
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 px-1">
                        <span className="text-[9px] font-600 tracking-widest text-primary uppercase">{release.type} · {release.year}</span>
                        <p className="text-sm font-700 text-foreground mt-0.5 truncate">{release.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{release.artist}</p>
                      </div>

                      {/* Expanded streaming links */}
                      {selectedRelease?.id === release.id && (
                        <div className="mt-2 p-3 rounded-xl bg-card border border-primary/20 animate-slide-up">
                          <p className="text-[10px] font-600 text-muted-foreground uppercase tracking-wider mb-2">Stream on</p>
                          <div className="flex flex-col gap-1.5">
                            {Object.entries(release.links).map(([platform, href]) => (
                              <a
                                key={platform}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-muted/40 hover:bg-muted/70 text-xs font-500 text-muted-foreground hover:text-foreground transition-all capitalize"
                                onClick={(e) => e.stopPropagation()}>
                                <ExternalLink size={10} />
                                {platform === 'apple' ? 'Apple Music' : platform === 'soundcloud' ? 'SoundCloud' : platform === 'audiomack' ? 'Audiomack' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* Stream everywhere */}
      <section className="py-16 bg-secondary/20 border-y border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Available On</p>
          <h2 className="text-3xl font-800 text-foreground mb-8">Stream Everywhere</h2>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {platforms.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border hover:border-primary/40 text-sm font-500 text-muted-foreground hover:text-foreground transition-all active:scale-95">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                {p.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
